import {
  SearchableVendorTaxonomyKey,
  VENUES_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  BANDS_DJS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PLANNERS_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';
import { Filter } from '@zola/tracking-contracts/src/objects';

import { FacetChildKey } from '~/types/facets';
import { AllFacetParentKey } from '~/types/types';

export interface SearchResultsPageFilter {
  key: AllFacetParentKey;
  /**
   * Filters that aren't important are hidden until the filters bar is expanded
   */
  isImportant?: boolean;
  /**
   * Only display the filter if this child key was selected
   */
  dependsOnKey?: FacetChildKey;

  childOrder?: FacetChildKey[];

  trackingFilter?: Filter['type'];
}

const isImportant = true;

/**
 * This is the meta that drives the filters on the SRP.  These are the
 * keys of facets + special filters (sorting, price, availability, capacity)
 * and they are in the order that product wanted the filters displayed.
 */
export const filtersByVendorCategory = getVendorTaxonomySwitchFunc<
  SearchableVendorTaxonomyKey,
  SearchResultsPageFilter[]
>({
  [VENUES_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'price', isImportant },
    { key: 'availability', isImportant },
    { key: 'capacity', isImportant },
    { key: 'venue-setting', isImportant, trackingFilter: 'SETTING' },
    { key: 'target-couple-venue-preference', isImportant, trackingFilter: 'TYPE' },
    { key: 'venue-service-level', isImportant, trackingFilter: 'SERVICE_LEVEL' },
    { key: 'award-groups', isImportant },
    { key: 'services-offered', trackingFilter: 'SERVICES' },
    {
      key: 'rental-services',
      dependsOnKey: 'services-offered-event-rentals',
      trackingFilter: 'RENTALS',
    },
    {
      key: 'accommodations-services',
      dependsOnKey: 'services-offered-on-site-accomodations',
      trackingFilter: 'ACCOMMODATIONS',
    },
    {
      key: 'transportation-services',
      dependsOnKey: 'services-offered-transportation',
      trackingFilter: 'TRANSPORTATION',
    },
    { key: 'event-types', trackingFilter: 'EVENT_TYPES' },
  ],

  [PHOTOGRAPHERS_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'price', isImportant },
    { key: 'location', isImportant },
    { key: 'photography-style', isImportant, trackingFilter: 'STYLE' },
    { key: 'package-components-services-offered', isImportant, trackingFilter: 'SERVICES' },
    { key: 'award-groups', isImportant },
    { key: 'photo-format', trackingFilter: 'PHOTO_FORMAT' },
    { key: 'package-deliverables-offered', trackingFilter: 'DELIVERABLES' },
  ],

  [VIDEOGRAPHERS_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'price', isImportant },
    { key: 'location', isImportant },
    { key: 'videography-style', isImportant, trackingFilter: 'STYLE' },
    { key: 'award-groups', isImportant },
    { key: 'videographer-package-components', trackingFilter: 'COMPONENTS' },
    { key: 'videographer-deliverables', trackingFilter: 'DELIVERABLES' },
  ],

  [FLORISTS_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'price', isImportant },
    { key: 'location', isImportant },
    { key: 'florist-style', isImportant, trackingFilter: 'STYLE' },
    { key: 'florist-service-level', isImportant, trackingFilter: 'SERVICE_LEVEL' },
    { key: 'florist-floral-arrangement', isImportant, trackingFilter: 'ARRANGEMENT' },
    { key: 'award-groups', isImportant },
    { key: 'florist-ethical-areas', trackingFilter: 'ETHICAL' },
    { key: 'florist-arrangement-types', trackingFilter: 'STYLING' },
  ],

  [CATERING_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'location', isImportant },
    { key: 'menu-formats-offered', isImportant, trackingFilter: 'MENUS' },
    { key: 'caterers-cuisine-types', isImportant, trackingFilter: 'FOOD_TYPES' },
    { key: 'caterer-dietary-restrictions', isImportant, trackingFilter: 'ACCOMMODATIONS' },
    { key: 'award-groups', isImportant },
    { key: 'menu-types', trackingFilter: 'MENUS' },
    { key: 'beverages-related-services', trackingFilter: 'BEVERAGES' },
    { key: 'caterer-drink-types', trackingFilter: 'DRINK_TYPES' },
  ],

  [BAR_SERVICES_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'location', isImportant },
    { key: 'caterer-drink-types', isImportant, trackingFilter: 'DRINK_TYPES' },
    { key: 'award-groups', isImportant },
  ],

  [CAKES_DESSERTS_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'price', isImportant },
    { key: 'location', isImportant },
    { key: 'bakers-product-types', isImportant, trackingFilter: 'FOOD_TYPES' },
    { key: 'bakers-dietary-accommodations', isImportant, trackingFilter: 'ACCOMMODATIONS' },
    { key: 'award-groups', isImportant },
  ],

  [BANDS_DJS_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'price', isImportant },
    { key: 'location', isImportant },
    { key: 'musician-type', isImportant, trackingFilter: 'TYPE' },
    { key: 'music-genre', isImportant, trackingFilter: 'GENRE' },
    { key: 'award-groups', isImportant },
    { key: 'musician-services', trackingFilter: 'SERVICES' },
    {
      key: 'music-band-acts',
      dependsOnKey: 'musician-type-live-band-or-ensemble',
      trackingFilter: 'BAND',
    },
    {
      key: 'music-solo-acts',
      dependsOnKey: 'musician-type-live-solo-performer',
      trackingFilter: 'SOLO',
    },
    { key: 'music-djs', dependsOnKey: 'musician-type-dj', trackingFilter: 'DJS' },
    {
      key: 'musician-category',
      dependsOnKey: 'musician-type-ceremony-only-musician',
      trackingFilter: 'CEREMONY',
    },
  ],

  [HAIR_MAKEUP_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'price', isImportant },
    { key: 'location', isImportant },
    { key: 'hair-beauty-service-category', isImportant, trackingFilter: 'SERVICES' },
    { key: 'hair-beauty-style', isImportant, trackingFilter: 'STYLE' },
    { key: 'award-groups', isImportant },
    {
      key: 'beauty-services-makeup',
      dependsOnKey: 'hair-beauty-service-category-makeup',
      trackingFilter: 'MAKEUP',
    },
    {
      key: 'beauty-services-hair',
      dependsOnKey: 'hair-beauty-service-category-hair',
      trackingFilter: 'HAIR',
    },
    {
      key: 'beauty-services-spa',
      dependsOnKey: 'hair-beauty-service-category-spa-treatments',
      trackingFilter: 'SPA',
    },
    { key: 'beauty-services-general', trackingFilter: 'GENERAL' },
    {
      key: 'beauty-services-styling',
      dependsOnKey: 'hair-beauty-service-category-styling',
      trackingFilter: 'STYLING',
    },
    {
      key: 'beauty-services-henna',
      dependsOnKey: 'hair-beauty-service-category-henna-art',
      trackingFilter: 'HENNA',
    },
    {
      key: 'beauty-services-fitness-wellness',
      dependsOnKey: 'hair-beauty-service-category-fitness-wellness',
      trackingFilter: 'FITNESS',
    },
  ],

  [PLANNERS_TAXONOMY_KEY]: [
    { key: 'sorting', isImportant },
    { key: 'price', isImportant },
    { key: 'location', isImportant },
    { key: 'planner-service-level', isImportant, trackingFilter: 'SERVICES' },
    { key: 'award-groups', isImportant },
  ],
});
